import { Action, type Role } from "@pilatestime/shared-types";
import {
	useQuery,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	ArrowRightLeft,
	Calendar,
	Mail,
	Shield,
	Trash2,
	User,
	X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { fetchSchedulesByStaffMemberQueryOptions } from "@/api/schedules";
import {
	fetchStaffMemberByIdQueryOptions,
	useRemoveStaffMember,
	useRevokeInvitation,
} from "@/api/staff";
import { ConfirmationWarningModal } from "@/components/ConfirmationWarningModal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import {
	getOrganizationRoleIcon,
	getOrganizationRoleLabel,
} from "@/utils/roleUtils";

const staffOverviewSearchSchema = z.object({
	type: z.enum(["staff", "invitation"]).optional().default("staff"),
	email: z.string().optional(),
	displayName: z.string().optional(),
	role: z.string().optional(),
	expiresAt: z.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/staff/$staffId/",
)({
	validateSearch: staffOverviewSearchSchema,
	loader: async ({ context }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		return { userId, token };
	},
	component: StaffOverviewPage,
});

function StaffOverviewPage() {
	const { orgId, staffId } = Route.useParams();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { userId, token } = Route.useLoaderData();

	const isInvitation = search.type === "invitation";

	if (isInvitation) {
		return (
			<InvitationOverview
				orgId={orgId}
				invitationId={staffId}
				token={token}
				email={search.email}
				displayName={search.displayName}
				role={search.role}
				expiresAt={search.expiresAt}
				onNavigateBack={() =>
					navigate({
						to: "/app/org/$orgId/staff",
						params: { orgId },
					})
				}
			/>
		);
	}

	return (
		<StaffMemberOverview
			orgId={orgId}
			staffId={staffId}
			userId={userId}
			token={token}
			onNavigateBack={() =>
				navigate({
					to: "/app/org/$orgId/staff",
					params: { orgId },
				})
			}
		/>
	);
}

// --- Staff Member Overview ---

interface StaffMemberOverviewProps {
	orgId: string;
	staffId: string;
	userId: string;
	token: string;
	onNavigateBack: () => void;
}

function StaffMemberOverview({
	orgId,
	staffId,
	userId,
	token,
	onNavigateBack,
}: StaffMemberOverviewProps) {
	const { t } = useTranslation();
	const { formatDate } = useDateFormatter();
	const navigate = useNavigate();
	const can = useCan();
	const canManageStaff = can(Action.MANAGE_STAFF);
	const canManageSchedules = can(Action.MANAGE_SCHEDULES);

	const { data: staffMember } = useSuspenseQuery(
		fetchStaffMemberByIdQueryOptions(userId, token, orgId, staffId),
	);

	const isOwner = staffMember.role === "ORG_OWNER";
	const showRemoveButton = canManageStaff && !isOwner;
	const showMigrateButton = canManageSchedules && !isOwner;

	const queryClient = useQueryClient();
	const [showRemoveModal, setShowRemoveModal] = useState(false);
	const removeStaffMutation = useRemoveStaffMember();

	const staffSchedulesQueryOptions = fetchSchedulesByStaffMemberQueryOptions(
		userId,
		token,
		orgId,
		staffId,
		{ hasActiveFutureSessions: true },
	);

	// Fetch schedules when modal is open
	const { data: staffSchedules, isLoading: isLoadingSchedules } = useQuery({
		...staffSchedulesQueryOptions,
		enabled: showRemoveModal,
	});

	const handleCloseRemoveModal = () => {
		setShowRemoveModal(false);
		queryClient.removeQueries({
			queryKey: staffSchedulesQueryOptions.queryKey,
		});
	};

	const generateWarnings = useCallback(() => {
		if (!staffSchedules || staffSchedules.length === 0) {
			return undefined;
		}

		return [
			{
				title: t("staff.remove.schedulesWarning", {
					count: staffSchedules.length,
				}),
				message: t("staff.remove.schedulesBlockedMessage"),
				points: staffSchedules.map((schedule) => ({
					text: schedule.name,
					href: `/app/org/${orgId}/schedules/${schedule.id}`,
				})),
			},
		];
	}, [staffSchedules, t]);

	const handleConfirmRemove = async () => {
		await removeStaffMutation.mutateAsync({
			token,
			organizationId: orgId,
			staffUserId: staffId,
		});
		onNavigateBack();
	};

	const staffName =
		staffMember.firstName && staffMember.lastName
			? `${staffMember.firstName} ${staffMember.lastName}`
			: staffMember.email;

	return (
		<div className="h-full flex flex-col space-y-6">
			{/* Quick Actions */}
			{(showRemoveButton || showMigrateButton) && (
				<Card className="border-0 shadow-lg bg-gradient-to-r from-primary/5 to-primary/10">
					<CardContent className="p-4">
						<div className="flex flex-wrap gap-3">
							{showMigrateButton && (
								<Button
									variant="default"
									size="sm"
									onClick={() =>
										navigate({
											to: "/app/org/$orgId/staff/$staffId/migrate-schedules",
											params: { orgId, staffId },
										})
									}
									className="gap-2"
								>
									<ArrowRightLeft className="h-4 w-4" />
									{t("staff.overview.quickActions.migrateSchedules")}
								</Button>
							)}
							{showRemoveButton && (
								<Button
									variant="default"
									size="sm"
									onClick={() => setShowRemoveModal(true)}
									className="gap-2"
								>
									<Trash2 className="h-4 w-4" />
									{t("staff.overview.quickActions.removeStaffMember")}
								</Button>
							)}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Staff Information Card */}
			<Card className="border-0 shadow-lg">
				<CardContent className="p-0">
					<div className="p-4 border-b border-base-200 flex items-center gap-2">
						<User className="h-5 w-5 text-primary" />
						<h3 className="font-semibold">
							{t("staff.overview.personalInformation")}
						</h3>
					</div>
					<div className="p-4 space-y-4">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							<div className="flex items-center gap-2 text-sm">
								<User className="h-4 w-4 text-base-content/60 flex-shrink-0" />
								<div>
									<span className="text-xs text-base-content/60 block">
										{t("staff.form.name")}
									</span>
									<span className="font-medium">{staffName}</span>
								</div>
							</div>

							{staffMember.email && (
								<div className="flex items-center gap-2 text-sm">
									<Mail className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div className="min-w-0">
										<span className="text-xs text-base-content/60 block">
											{t("staff.form.email")}
										</span>
										<span className="truncate block">{staffMember.email}</span>
									</div>
								</div>
							)}

							<div className="flex items-center gap-2 text-sm">
								<Shield className="h-4 w-4 text-base-content/60 flex-shrink-0" />
								<div>
									<span className="text-xs text-base-content/60 block">
										{t("staff.overview.role")}
									</span>
									<div className="flex items-center gap-1">
										{getOrganizationRoleIcon(staffMember.role)}
										<span className="font-medium">
											{getOrganizationRoleLabel(staffMember.role, t)}
										</span>
									</div>
								</div>
							</div>

							{staffMember.joinedAt && (
								<div className="flex items-center gap-2 text-sm">
									<Calendar className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div>
										<span className="text-xs text-base-content/60 block">
											{t("staff.overview.joinedAt")}
										</span>
										<span className="font-medium">
											{formatDate(new Date(staffMember.joinedAt).getTime())}
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Remove Staff Member Modal */}
			<ConfirmationWarningModal
				isOpen={showRemoveModal}
				onClose={handleCloseRemoveModal}
				onConfirm={handleConfirmRemove}
				title={t("staff.remove.title")}
				subtitle={t("staff.remove.description", { name: staffName })}
				warnings={generateWarnings()}
				confirmButtonText={t("staff.remove.confirm")}
				loadingText={t("staff.toasts.removing")}
				isLoading={removeStaffMutation.isPending || isLoadingSchedules}
				disableConfirm={(staffSchedules?.length ?? 0) > 0}
				variant="danger"
			/>
		</div>
	);
}

// --- Invitation Overview ---

interface InvitationOverviewProps {
	orgId: string;
	invitationId: string;
	token: string;
	email?: string;
	displayName?: string;
	role?: string;
	expiresAt?: string;
	onNavigateBack: () => void;
}

function InvitationOverview({
	orgId,
	invitationId,
	token,
	email,
	displayName,
	role,
	expiresAt,
	onNavigateBack,
}: InvitationOverviewProps) {
	const { t } = useTranslation();
	const { formatDate } = useDateFormatter();
	const can = useCan();
	const canManageStaff = can(Action.MANAGE_STAFF);

	const [showRevokeModal, setShowRevokeModal] = useState(false);
	const revokeInvitationMutation = useRevokeInvitation();

	const handleConfirmRevoke = async () => {
		await revokeInvitationMutation.mutateAsync({
			token,
			organizationId: orgId,
			invitationId,
		});
		onNavigateBack();
	};

	const invitationName = displayName || email || "";

	return (
		<div className="h-full flex flex-col space-y-6">
			{/* Quick Actions */}
			{canManageStaff && (
				<Card className="border-0 shadow-lg bg-gradient-to-r from-primary/5 to-primary/10">
					<CardContent className="p-4">
						<div className="flex flex-wrap gap-3">
							<Button
								variant="default"
								size="sm"
								onClick={() => setShowRevokeModal(true)}
								className="gap-2"
							>
								<X className="h-4 w-4" />
								{t("staff.overview.quickActions.revokeInvitation")}
							</Button>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Invitation Information Card */}
			<Card className="border-0 shadow-lg">
				<CardContent className="p-0">
					<div className="p-4 border-b border-base-200 flex items-center gap-2">
						<Mail className="h-5 w-5 text-primary" />
						<h3 className="font-semibold">
							{t("staff.overview.invitationInfo.title")}
						</h3>
					</div>
					<div className="p-4 space-y-4">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
							{email && (
								<div className="flex items-center gap-2 text-sm">
									<Mail className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div className="min-w-0">
										<span className="text-xs text-base-content/60 block">
											{t("staff.overview.invitationInfo.email")}
										</span>
										<span className="truncate block">{email}</span>
									</div>
								</div>
							)}

							{displayName && (
								<div className="flex items-center gap-2 text-sm">
									<User className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div>
										<span className="text-xs text-base-content/60 block">
											{t("staff.form.name")}
										</span>
										<span className="font-medium">{displayName}</span>
									</div>
								</div>
							)}

							{role && (
								<div className="flex items-center gap-2 text-sm">
									<Shield className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div>
										<span className="text-xs text-base-content/60 block">
											{t("staff.overview.invitationInfo.role")}
										</span>
										<div className="flex items-center gap-1">
											{getOrganizationRoleIcon(role as Role)}
											<span className="font-medium">
												{getOrganizationRoleLabel(role as Role, t)}
											</span>
										</div>
									</div>
								</div>
							)}

							{expiresAt && (
								<div className="flex items-center gap-2 text-sm">
									<Calendar className="h-4 w-4 text-base-content/60 flex-shrink-0" />
									<div>
										<span className="text-xs text-base-content/60 block">
											{t("staff.overview.invitationInfo.expiresAt")}
										</span>
										<span className="font-medium">
											{formatDate(new Date(expiresAt).getTime())}
										</span>
									</div>
								</div>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Revoke Invitation Modal */}
			<ConfirmationWarningModal
				isOpen={showRevokeModal}
				onClose={() => setShowRevokeModal(false)}
				onConfirm={handleConfirmRevoke}
				title={t("staff.overview.revokeInvitation.title")}
				subtitle={t("staff.overview.revokeInvitation.description", {
					name: invitationName,
				})}
				confirmButtonText={t("staff.invitation.revoke.confirm")}
				loadingText={t("staff.toasts.revoking")}
				isLoading={revokeInvitationMutation.isPending}
				variant="danger"
			/>
		</div>
	);
}
